import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "./lib/utils";

export const HeroBackground = () => {
    const [boxes, setBoxes] = useState([]);

    useEffect(() => {
        const boxData = [
            {
                text: "Decision Making",
                top: "15%", left: "10%",
                description: "Clarity emerges when intelligence meets intuition."
            },
            {
                text: "Human Oversight",
                top: "25%", left: "80%",
                description: "Technology that amplifies our values, rather than replacing them."
            },
            {
                text: "Autonomous Agents",
                top: "60%", left: "5%",
                description: "Systems that work tirelessly to build the future we imagine."
            },
            {
                text: "Compliance",
                top: "70%", left: "85%",
                description: "Harmony between innovation and the rules that govern trust."
            },
            {
                text: "Security",
                top: "10%", left: "60%",
                description: "A foundation of trust so solid, it becomes invisible."
            },
            {
                text: "Global Scale",
                top: "80%", left: "15%",
                description: "Ideas that ripple across borders to touch every life."
            },
            {
                text: "Research Driven",
                top: "40%", left: "90%",
                description: "Exploring the unknown to solve the problems of today."
            },
            {
                text: "Neural Networks",
                top: "50%", left: "4%",
                description: "Weaving complexity into simple, elegant solutions."
            },
            {
                text: "Belief",
                top: "20%", left: "45%",
                isDemo: true,
                description: "The core conviction that technology must serve humanity's highest potential."
            },
            {
                text: "Why Us?",
                top: "65%", left: "75%",
                description: "We don't just predict the future; we engineer the fundamental layers that enable it."
            },
            {
                text: "The Uncommon",
                top: "75%", left: "40%",
                description: "Venturing where we not just create value—we transform fundamental processes."
            },
            {
                text: "The Nerds",
                top: "12%", left: "28%",
                description: "Unapologetic intellect. Relentless curiosity. Building the impossible."
            },
        ];
        setBoxes(boxData);
    }, []);

    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
            {/* Grid Pattern */}
            <div
                className="absolute inset-0 opacity-[0.2]"
                style={{
                    backgroundImage: 'radial-gradient(#94a3b8 1px, transparent 1px)',
                    backgroundSize: '40px 40px'
                }}
            ></div>

            {boxes.map((box, idx) => (
                <ScatteredBox
                    key={idx}
                    text={box.text}
                    description={box.description}
                    top={box.top}
                    left={box.left}
                    delay={idx * 0.2}
                    isDemo={box.isDemo}
                />
            ))}
        </div>
    );
};

const ScatteredBox = ({ text, description, top, left, delay, isDemo }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [demoCardActive, setDemoCardActive] = useState(false);
    const [demoMouseIn, setDemoMouseIn] = useState(false);

    useEffect(() => {
        if (!isDemo) return;

        // Sequence:
        // 1. Mouse moves IN (duration ~0.8s)
        // 2. Card highlights
        // 3. Pause
        // 4. Card un-highlights
        // 5. Mouse moves OUT

        const cycle = () => {
            // 1. Move Mouse In
            setDemoMouseIn(true);

            // 2. Wait for travel (0.8s), then Highlight Card
            setTimeout(() => {
                setDemoCardActive(true);
            }, 800);

            // 3. End of "Hover" phase (e.g. 3s later)
            setTimeout(() => {
                // Unhighlight and Move Out
                setDemoCardActive(false);
                setDemoMouseIn(false);
            }, 3800);
        };

        // Initial Start Delay
        const startTimeout = setTimeout(() => {
            cycle();
            // Total Cycle Length: 3800ms + Wait time for next loop (e.g. 2000ms pause = 5800ms total)
            const interval = setInterval(cycle, 6000);
            return () => clearInterval(interval);
        }, 2000);

        return () => clearTimeout(startTimeout);
    }, [isDemo]);

    const active = isHovered || (isDemo && demoCardActive);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: delay }}
            className={cn(
                "absolute pointer-events-auto group",
                active ? "z-[60]" : "z-10"
            )}
            style={{ top, left }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div
                className={cn(
                    "relative px-4 py-2 border transition-all duration-300 cursor-pointer",
                    active
                        ? "bg-blue-500/10 border-blue-400 z-50 text-blue-600 scale-105"
                        : "bg-white/40 border-gray-200/60 backdrop-blur-sm text-gray-400 hover:text-gray-600",
                    "text-[10px] font-mono uppercase tracking-widest",
                    // Extended lines
                    !active && "before:absolute before:top-[-20%] before:left-[-1px] before:w-[1px] before:h-[140%] before:bg-gray-200/60",
                    !active && "after:absolute after:left-[-20%] after:top-[-1px] after:h-[1px] after:w-[140%] after:bg-gray-200/60"
                )}
            >
                {/* Additional crosshairs for corners (visible when not hovered) */}
                {!active && (
                    <>
                        <div className="absolute top-0 right-0 w-[1px] h-[140%] -translate-y-[20%] bg-gray-200/60" />
                        <div className="absolute bottom-0 left-0 w-[140%] h-[1px] -translate-x-[20%] bg-gray-200/60" />
                    </>
                )}

                {text}
            </div>

            <AnimatePresence>
                {active && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.9 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-3 w-56 bg-slate-50/90 backdrop-blur-md border border-slate-200 p-4 rounded-xl shadow-[0_8px_32px_rgba(30,41,59,0.12)] z-[100]"
                    >
                        <p className="text-slate-800 text-xs font-medium leading-relaxed font-sans italic">
                            "{description}"
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Demo Mouse Cursor */}
            {isDemo && (
                <DemoMouse show={demoMouseIn} />
            )}
        </motion.div>
    );
};

const DemoMouse = ({ show }) => {
    return (
        <motion.div
            className="absolute z-[100] pointer-events-none"
            initial={{ x: -100, y: -50, opacity: 0 }}
            animate={{
                x: show ? 10 : -100,
                y: show ? 0 : -50,
                opacity: 1 // Always visible, just moving away
            }}
            transition={{
                duration: 0.8,
                ease: "easeInOut"
            }}
        >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.65376 12.3673H5.46026L5.31717 12.4976L0.500002 16.8829L0.500002 1.19169L11.7841 12.3673H5.65376Z" fill="#3B82F6" stroke="white" />
            </svg>
        </motion.div>
    );
};
